import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import CategorySidebar from "@/components/feature/CategorySidebar";
import EmptyState from "@/components/base/EmptyState";
import { ResourceCard } from "@/components/feature/ContentCards";
import { resourceList, resourceTypes } from "@/mocks/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const STEP = 6;

const typeIntro: Record<string, string> = {
  all: "影视、游戏、漫画三类资源汇总，可按地区、年份与推荐度筛选，快速找到想看的作品。",
  film: "成年向电影与剧集资源介绍，包含题材标签、适看提示与完整剧情概述。",
  game: "叙事游戏、文字冒险与独立作品资源介绍，标注玩法类型与适看强度。",
  comic: "单行本、连载与短篇漫画资源介绍，附阅读顺序建议与分卷信息。",
};

const regionOptions = ["全部", "欧美", "日本", "韩国", "国产"];
const ratingOptions = [
  { key: "all", label: "全部推荐度" },
  { key: "9", label: "9 分以上" },
  { key: "8.5", label: "8.5 分以上" },
  { key: "8", label: "8 分以上" },
];
const sortOptions = [
  { key: "latest", label: "最新上线" },
  { key: "hot", label: "最热门" },
  { key: "favorites", label: "收藏最多" },
];

export default function ResourcesPage() {
  const { type } = useParams();
  const activeType = type && ["film", "game", "comic"].includes(type) ? type : "all";

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [rating, setRating] = useState("all");
  const [sort, setSort] = useState("latest");
  const [visible, setVisible] = useState(STEP);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const typeLabel = activeType === "all" ? "全部" : resourceTypes.find((item) => item.key === activeType)?.label ?? "全部";

  useDocumentMeta({
    title: `${typeLabel}资源 - 成人影视游戏漫画精选 - 18禁`,
    description: `18禁${typeLabel}资源汇总：${typeIntro[activeType]}所有条目均为虚构演示，不含真实下载。`,
    keywords: `${typeLabel}资源,18禁资源,成人影视资源,成人游戏推荐,成人漫画推荐`,
  });

  const years = useMemo(() => {
    const values = Array.from(new Set(resourceList.map((item) => item.year))).sort((a, b) => b - a);
    return ["全部", ...values.map(String)];
  }, []);

  useEffect(() => {
    setVisible(STEP);
  }, [activeType, region, year, rating, sort]);

  const filtered = useMemo(() => {
    const list = resourceList.filter((item) => {
      if (activeType !== "all" && item.type !== activeType) return false;
      if (region !== "全部" && item.region !== region) return false;
      if (year !== "全部" && String(item.year) !== year) return false;
      if (rating !== "all" && item.rating < Number(rating)) return false;
      return true;
    });
    const sorted = [...list];
    if (sort === "latest") sorted.sort((a, b) => b.year - a.year || b.rating - a.rating);
    if (sort === "hot") sorted.sort((a, b) => b.rating - a.rating);
    if (sort === "favorites") sorted.sort((a, b) => b.favorites - a.favorites);
    return sorted;
  }, [activeType, region, year, rating, sort]);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setRating("all");
    setSort("latest");
    setVisible(STEP);
  };

  const hasFilter = region !== "全部" || year !== "全部" || rating !== "all";

  return (
    <div>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <nav className="mb-5 flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm text-foreground-300" aria-hidden="true"></i>
            <Link to="/resources" className="cursor-pointer transition hover:text-primary-700">
              资源库
            </Link>
            {activeType !== "all" ? (
              <>
                <i className="ri-arrow-right-s-line text-sm text-foreground-300" aria-hidden="true"></i>
                <span className="text-foreground-700">{typeLabel}资源</span>
              </>
            ) : null}
          </nav>
          <h1 className="font-heading text-2xl leading-snug text-foreground-950 md:text-[32px]">
            {activeType === "all" ? "资源库" : `${typeLabel}资源`}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">{typeIntro[activeType]}</p>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Link
              to="/resources"
              className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-1.5 text-xs transition ${
                activeType === "all"
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300"
              }`}
            >
              全部类型
            </Link>
            {resourceTypes.map((item) => (
              <Link
                key={item.key}
                to={`/resources/${item.key}`}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-1.5 text-xs transition ${
                  activeType === item.key
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        <div className="grid gap-8 lg:grid-cols-[248px_1fr] lg:gap-10">
          <aside className="lg:sticky lg:top-[118px] lg:self-start">
            <CategorySidebar />
          </aside>

          <div className="min-w-0">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-foreground-500">
                共 <span className="font-medium text-foreground-800">{filtered.length}</span> 条资源 · 已显示{" "}
                {Math.min(visible, filtered.length)} 条
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFiltersOpen((value) => !value)}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-700 md:hidden"
                >
                  <i className="ri-filter-3-line text-sm" aria-hidden="true"></i>
                  筛选
                </button>
                <label className="flex items-center gap-2 text-xs text-foreground-500">
                  排序
                  <select
                    value={sort}
                    onChange={(event) => setSort(event.target.value)}
                    className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-2 text-xs text-foreground-700 outline-none focus:border-primary-400"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className={`${filtersOpen ? "block" : "hidden"} mb-6 rounded-lg border border-background-200 bg-background-50 p-4 md:block`}>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <p className="mb-2.5 text-[11px] font-medium tracking-[0.16em] text-foreground-500">地区</p>
                  <div className="flex flex-wrap gap-1.5">
                    {regionOptions.map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setRegion(item)}
                        className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-[11px] transition ${
                          region === item
                            ? "border-primary-400 bg-primary-50 text-primary-700"
                            : "border-background-300/80 bg-background-50 text-foreground-600 hover:border-primary-200"
                        }`}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2.5 text-[11px] font-medium tracking-[0.16em] text-foreground-500">年份</p>
                  <div className="flex flex-wrap gap-1.5">
                    {years.map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setYear(item)}
                        className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-[11px] transition ${
                          year === item
                            ? "border-primary-400 bg-primary-50 text-primary-700"
                            : "border-background-300/80 bg-background-50 text-foreground-600 hover:border-primary-200"
                        }`}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2.5 text-[11px] font-medium tracking-[0.16em] text-foreground-500">推荐度</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ratingOptions.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setRating(option.key)}
                        className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-[11px] transition ${
                          rating === option.key
                            ? "border-accent-400 bg-accent-100 text-accent-800"
                            : "border-background-300/80 bg-background-50 text-foreground-600 hover:border-accent-300"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {hasFilter ? (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-4 flex cursor-pointer items-center gap-1 text-[11px] text-foreground-500 transition hover:text-primary-700"
                >
                  <i className="ri-refresh-line text-xs" aria-hidden="true"></i>
                  重置筛选条件
                </button>
              ) : null}
            </div>

            {filtered.length ? (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-5">
                  {filtered.slice(0, visible).map((item) => (
                    <ResourceCard key={item.id} item={item} />
                  ))}
                </div>
                {visible < filtered.length ? (
                  <div className="mt-8 flex flex-col items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setVisible((value) => value + STEP)}
                      className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition hover:border-primary-300 hover:text-primary-700"
                    >
                      <i className="ri-add-line text-base" aria-hidden="true"></i>
                      加载更多资源
                    </button>
                    <span className="text-[11px] text-foreground-400">
                      还有 {filtered.length - visible} 条未显示
                    </span>
                  </div>
                ) : (
                  <p className="mt-8 text-center text-[11px] tracking-wider text-foreground-400">已经到底了</p>
                )}
              </>
            ) : (
              <EmptyState
                title="没有符合条件的资源"
                description="当前地区、年份与推荐度组合下没有找到条目。可以尝试减少筛选条件，或直接查看全部资源。"
                actionLabel="重置筛选条件"
                onAction={resetFilters}
                secondaryLabel="查看全部资源"
                secondaryTo="/resources"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}